import {
  ArrowLeft,
  BarChart3,
  CheckCircle,
  Clock,
  FileText,
  Receipt,
  TrendingUp,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';

type Props = {
  isLoading: boolean;
};

export function InitialStage({ isLoading }: Props) {
  return (
    <div className="col-span-12 flex min-h-[500px] flex-col items-center justify-center p-6">
      {!isLoading && (
        <div className="w-full max-w-4xl">
          {/* Main Container */}
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-white/80 to-slate-50/80 p-8 shadow-xl backdrop-blur-sm dark:from-slate-900/80 dark:to-slate-800/80">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-30">
              <svg
                width="60"
                height="60"
                viewBox="0 0 60 60"
                xmlns="http://www.w3.org/2000/svg"
                className="h-full w-full"
              >
                <g fill="none" fillRule="evenodd">
                  <g fill="rgba(59,130,246,0.1)">
                    <circle cx="30" cy="30" r="2" />
                  </g>
                </g>
              </svg>
            </div>

            {/* Floating Elements */}
            <div className="absolute -right-4 -top-4 flex h-8 w-8 items-center justify-center rounded-full bg-blue-500/20 backdrop-blur-sm">
              <Receipt className="h-4 w-4 text-blue-600" />
            </div>
            <div className="absolute -bottom-4 -left-4 flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500/20 backdrop-blur-sm">
              <TrendingUp className="h-4 w-4 text-emerald-600" />
            </div>

            {/* Main Content */}
            <div className="relative text-center">
              {/* Hero Icon */}
              <div className="mb-6 flex justify-center">
                <div className="relative">
                  <div className="flex h-24 w-24 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg">
                    <FileText className="h-12 w-12 text-white" />
                  </div>
                  {/* Floating accent icons */}
                  <div className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500 shadow-md">
                    <CheckCircle className="h-3 w-3 text-white" />
                  </div>
                  <div className="absolute -bottom-2 -left-2 flex h-6 w-6 items-center justify-center rounded-full bg-amber-500 shadow-md">
                    <Clock className="h-3 w-3 text-white" />
                  </div>
                </div>
              </div>

              {/* Title and Description */}
              <h1 className="mb-4 text-4xl font-bold text-slate-900 dark:text-slate-100">
                فواتير الشراء
              </h1>
              <p className="mb-8 max-w-md text-lg text-slate-600 dark:text-slate-400">
                برجاء اختيار فاتورة من القائمة الجانبية لعرض التفاصيل والتحكم
              </p>

              {/* Feature Cards */}
              <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-3">
                <Card className="border-0 bg-gradient-to-br from-blue-50 to-indigo-100 shadow-md backdrop-blur-sm dark:from-blue-950/50 dark:to-indigo-900/50">
                  <CardContent className="flex items-center gap-3 p-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 shadow-md">
                      <FileText className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                        عرض الفواتير
                      </p>
                      <p className="text-xs text-slate-600 dark:text-slate-400">
                        تصفح جميع الفواتير
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 bg-gradient-to-br from-emerald-50 to-teal-100 shadow-md backdrop-blur-sm dark:from-emerald-950/50 dark:to-teal-900/50">
                  <CardContent className="flex items-center gap-3 p-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 shadow-md">
                      <BarChart3 className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                        تحليل البيانات
                      </p>
                      <p className="text-xs text-slate-600 dark:text-slate-400">
                        إحصائيات مفصلة
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 bg-gradient-to-br from-amber-50 to-orange-100 shadow-md backdrop-blur-sm dark:from-amber-950/50 dark:to-orange-900/50">
                  <CardContent className="flex items-center gap-3 p-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 shadow-md">
                      <TrendingUp className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                        إدارة متقدمة
                      </p>
                      <p className="text-xs text-slate-600 dark:text-slate-400">
                        تحكم كامل
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Call to Action */}
              <div className="rounded-xl border border-slate-200/50 bg-slate-50/50 p-4 backdrop-blur-sm dark:border-slate-700/50 dark:bg-slate-800/50">
                <div className="flex items-center justify-center gap-3">
                  <ArrowLeft className="h-5 w-5 text-slate-600 dark:text-slate-400" />
                  <div>
                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                      اختر فاتورة من القائمة
                    </p>
                    <p className="text-xs text-slate-600 dark:text-slate-400">
                      لعرض التفاصيل والتحكم في الفاتورة
                    </p>
                  </div>
                </div>
              </div>

              {/* Status Badges */}
              <div className="mt-6 flex justify-center gap-2">
                <Badge
                  variant="outline"
                  className="border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-700 dark:bg-blue-950/30 dark:text-blue-300"
                >
                  <FileText className="mr-1 h-3 w-3" />
                  فواتير متاحة
                </Badge>
                <Badge
                  variant="outline"
                  className="border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300"
                >
                  <CheckCircle className="mr-1 h-3 w-3" />
                  جاهز للعرض
                </Badge>
              </div>
            </div>
          </div>
        </div>
      )}

      {isLoading && (
        <div className="w-full max-w-md">
          <Card className="border-0 bg-gradient-to-br from-white/80 to-slate-50/80 shadow-lg backdrop-blur-sm dark:from-slate-900/80 dark:to-slate-800/80">
            <CardContent className="flex flex-col items-center justify-center p-8">
              {/* Loading Animation */}
              <div className="relative mb-6">
                <div className="relative flex h-16 w-16 items-center justify-center">
                  {/* Background glow */}
                  <div className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 opacity-20 blur-lg"></div>

                  {/* Main loading spinner */}
                  <div className="relative flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg">
                    <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/30 border-t-white"></div>
                  </div>

                  {/* Floating elements */}
                  <div className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 shadow-md">
                    <FileText className="h-2 w-2 text-white" />
                  </div>
                  <div className="absolute -bottom-1 -left-1 flex h-4 w-4 items-center justify-center rounded-full bg-amber-500 shadow-md">
                    <TrendingUp className="h-2 w-2 text-white" />
                  </div>
                </div>
              </div>

              <div className="text-center">
                <h3 className="mb-2 text-lg font-semibold text-slate-900 dark:text-slate-100">
                  جاري تحميل الفواتير
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  يرجى الانتظار بينما نقوم بجلب البيانات...
                </p>
              </div>

              {/* Progress indicator */}
              <div className="mt-6 flex items-center gap-2">
                <div className="h-1 w-16 animate-pulse rounded-full bg-gradient-to-r from-blue-500 to-indigo-600"></div>
                <div
                  className="h-1 w-12 animate-pulse rounded-full bg-gradient-to-r from-indigo-500 to-purple-600"
                  style={{ animationDelay: '0.2s' }}
                ></div>
                <div
                  className="h-1 w-8 animate-pulse rounded-full bg-gradient-to-r from-purple-500 to-pink-600"
                  style={{ animationDelay: '0.4s' }}
                ></div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
